// @givmo/compliance — a filing case's submission, decoded (CCM-R1-SUBMIT-WEB; the backend's
// `CCM-R1-SUBMIT`, `CCM-R1-SUBMIT-RESEND-GUARD` and `CCM-R1-SUBMIT-ZIP`).
//
// ══ WHAT THIS FILE READS ═══════════════════════════════════════════════════════════════════════
//
// Each tolerantly off `unknown`, in `compliance-decode.ts`'s style:
//
//   • one case's submission, `…/filing-cases/{id}/submission` (`ComplianceSubmissionOut`), which
//     every act on it (authorize, revoke, send, record what happened) answers too: whether Givmo
//     can email the filing and, if not, why, in the server's words; the files exactly as they
//     would go with the SHA-256 that binds them; the email's subject and body; the agency's
//     address; the fee as the form states it; the signed forms' request; the reply-contact choices
//     and who the email actually goes to here; the statement the act is made by; the case's latest
//     authorization; every send; and what the viewer can do now;
//   • the package as one zip, `…/submission/package` (`ComplianceSubmissionPackageFileOut`): a
//     short-lived link and the name it downloads as.
//
// CCM-R1-SUBMIT-HANDOFF adds, on the same read: how the agency's sources say the filing is sent
// (`instructions`: each way it takes it, in the rule's order, with the form's own printed words
// where they are the rule's destination; how the fee is paid; who signs; what comes back), what the
// organization recorded it sent itself (`handoffs`), and whether the viewer may record that. And
// CCM-R1-SUBMIT-SENT-SUMMARY: an authorization's checklist as it stood when it was authorized.
//
// CCM-ST2-SUBMIT-CITE adds where the agency's email address comes from (`destination.citation`):
// the agency's own sentence giving it, cited to the source of the rule's it was read from, with
// that source's page, short citation, date and link. Absent on an older backend, and absent while a
// rule cites none: the send review then keeps the line it drew before from `instructions`.
//
// CCM-R1-SUBMIT-MCP adds the organization's AI (J3; D-CCM-27 item 1 (A)): the words a person
// authorizes by to let their own AI send the package once, and how long that lasts
// (`connector_statement`); whether an authorization lets its author's AI send it; a send's
// `dispatched_through`, and the person's request to their AI as the AI relayed it (the
// organization's own account, which Givmo doesn't check); and whether the viewer's own AI may send
// it now. Each is absent on an older backend, and absent reads as no: no AI state ever shows on a
// guess.
//
// CCM-R1-RESULTS-WEB adds what came back after a send (contract §10; D-CCM-68): every report and
// record about a send (`evidence`), each with where it came from, and what is known about each send
// so far (`follow_through`), with who recorded the record that decides it, and on which day in the
// filing's own time zone; whether the viewer may record the agency's answer or say a record is
// wrong; and, on its own read, each case's latest send for the panel, the Calendar and Filings.
//
// The read names no person: an authorizer, a sender and whoever recorded what happened are user
// ids, named by the host's roster. The email's addresses ARE on the wire: they are what the
// organization authorizes, shown verbatim.
//
// ⚠ AN UNREADABLE VALUE NEVER RESOLVES TOWARDS THE MORE CONFIDENT ANSWER: a send whose state is
// unknown is neither sent nor not sent, an authorization whose state is unknown is not in force,
// and a "can" flag that is absent is no.

import { decodeDocumentVersion, type DocumentVersion } from './compliance-preparation-decode';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : [];
}

function list<T>(value: unknown, decode: (raw: unknown) => T | undefined): T[] {
  return Array.isArray(value)
    ? value.flatMap((raw) => {
        const decoded = decode(raw);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
}

// ── The vocabulary ─────────────────────────────────────────────────────────────────────────────

export const SUBMISSION_ATTEMPT_STATES = [
  'pending',
  'provider_accepted',
  'refused',
  'uncertain',
  'not_sent',
] as const;
/**
 * One send's outcome. `provider_accepted` is Givmo's email service taking the email for delivery:
 * never delivery, never the agency's acceptance.
 */
export type SubmissionAttemptState = (typeof SUBMISSION_ATTEMPT_STATES)[number];

export const SUBMISSION_EVIDENCE_KINDS = [
  'provider_accepted',
  'provider_delivered',
  'provider_bounced',
  'provider_dropped',
  'agency_acknowledgment',
  'agency_acceptance',
  'agency_rejection',
  'agency_correction_request',
  'organization_recorded',
  'desk_receipt',
] as const;
/**
 * What one piece of evidence about a send says. Each stays itself: the email service taking an email
 * is not its delivery, a delivery to the agency's server is not the agency's acknowledgment, and an
 * acknowledgment is not its acceptance.
 */
export type SubmissionEvidenceKind = (typeof SUBMISSION_EVIDENCE_KINDS)[number];

export const SUBMISSION_EVIDENCE_SOURCES = [
  'provider_event',
  'agency_message',
  'organization_upload',
  'desk_runbook',
] as const;
/** Where a piece of evidence came from: the organization's own record is never the agency's. */
export type SubmissionEvidenceSource = (typeof SUBMISSION_EVIDENCE_SOURCES)[number];

export const SUBMISSION_FOLLOW_THROUGHS = [
  'taken',
  'recorded_sent',
  'delivered',
  'not_delivered',
  'acknowledged',
  'rejected',
  'correction_requested',
  'confirmed',
  'uncertain',
] as const;
/** What is known so far about one send, after it left; each only what it says. */
export type SubmissionFollowThroughState = (typeof SUBMISSION_FOLLOW_THROUGHS)[number];

export const SUBMISSION_AUTHORIZATION_STATES = [
  'active',
  'consumed',
  'revoked',
  'expired',
] as const;
export type SubmissionAuthorizationState = (typeof SUBMISSION_AUTHORIZATION_STATES)[number];

export const FEE_STATEMENT_KINDS = [
  'no_fee',
  'organization_pays_agency',
  'amount_unresolved',
] as const;
/** The fee as the form's own fee item states it; Givmo never works one out. */
export type FeeStatementKind = (typeof FEE_STATEMENT_KINDS)[number];

export const REPLY_CONTACT_SOURCES = ['organization_email', 'authorizer_email'] as const;
/** The email on the organization's own form, or the authorizer's own sign-in email: never typed. */
export type ReplyContactSource = (typeof REPLY_CONTACT_SOURCES)[number];

export type PackageRole = 'signed_form' | 'attachment';

export const SUBMISSION_CHANNELS = [
  'postal_mail',
  'email',
  'online_portal',
  'in_person',
  'fax',
] as const;
/** A way the agency takes a filing, as its rule records it. */
export type SubmissionChannel = (typeof SUBMISSION_CHANNELS)[number];

export const HANDOFF_CONTENTS = ['package', 'other'] as const;
/** What the organization recorded it sent: the package as the case composed it, or a document of its own. */
export type HandoffContent = (typeof HANDOFF_CONTENTS)[number];

export const CHECKLIST_RECORD_STATUSES = ['attached', 'not_needed', 'open'] as const;
/** A checklist item's state as a record kept it (an authorization, or what was recorded as sent). */
export type ChecklistRecordStatus = (typeof CHECKLIST_RECORD_STATUSES)[number];

/** How a finding about an uncertain send is known; `provider_event` is never a person's. */
export type ReconciliationBasis =
  | 'copy_received'
  | 'provider_activity'
  | 'not_received'
  | 'provider_event';

/** The findings a person may record about an uncertain send. */
export type PersonReconciliationBasis = 'copy_received' | 'provider_activity' | 'not_received';

/** Why a send is uncertain: the email service's answer didn't settle it, or none was recorded. */
export type UncertainCause = 'provider_unclear' | 'stamp_missing';

/** Why the email service refused a send, so nothing reads a status number. */
export type SubmissionRefusalReason = 'provider_busy' | 'provider_refused' | 'not_built';

/**
 * The readiness and act refusals the page itself acts on (the server's `error_code`s). Any other
 * code is shown as its served message and nothing else.
 */
export const SUBMISSION_REFUSALS = {
  caseWithdrawn: 'COMPLIANCE_SUBMISSION_CASE_WITHDRAWN',
  casePredates: 'COMPLIANCE_SUBMISSION_CASE_PREDATES',
  filingNotOnRegister: 'COMPLIANCE_SUBMISSION_FILING_NOT_ON_REGISTER',
  ruleReplaced: 'COMPLIANCE_SUBMISSION_RULE_REPLACED',
  periodReplaced: 'COMPLIANCE_SUBMISSION_PERIOD_REPLACED',
  alreadySent: 'COMPLIANCE_SUBMISSION_ALREADY_SENT',
  noEmailChannel: 'COMPLIANCE_SUBMISSION_NO_EMAIL_CHANNEL',
  formUnsigned: 'COMPLIANCE_SUBMISSION_FORM_UNSIGNED',
  signedForAnotherChannel: 'COMPLIANCE_SUBMISSION_SIGNED_FOR_ANOTHER_CHANNEL',
  formNotPrepared: 'COMPLIANCE_SUBMISSION_FORM_NOT_PREPARED',
  checklistOpen: 'COMPLIANCE_SUBMISSION_CHECKLIST_OPEN',
  feeNotSendable: 'COMPLIANCE_SUBMISSION_FEE_NOT_SENDABLE',
  tooLarge: 'COMPLIANCE_SUBMISSION_TOO_LARGE',
  replyContactUnavailable: 'COMPLIANCE_SUBMISSION_REPLY_CONTACT_UNAVAILABLE',
  changed: 'COMPLIANCE_SUBMISSION_CHANGED',
  packageChanged: 'COMPLIANCE_SUBMISSION_PACKAGE_CHANGED',
  packageStale: 'COMPLIANCE_SUBMISSION_PACKAGE_STALE',
  attemptStale: 'COMPLIANCE_SUBMISSION_ATTEMPT_STALE',
  inProgress: 'COMPLIANCE_SUBMISSION_IN_PROGRESS',
  notInForce: 'COMPLIANCE_SUBMISSION_NOT_IN_FORCE',
  // CCM-R1-SUBMIT-HANDOFF: the refusals of a record of what the organization sent itself.
  handoffChannel: 'COMPLIANCE_SUBMISSION_HANDOFF_CHANNEL',
  handoffPackageStale: 'COMPLIANCE_SUBMISSION_HANDOFF_PACKAGE_STALE',
  handoffRecorded: 'COMPLIANCE_SUBMISSION_HANDOFF_RECORDED',
  handoffNoFile: 'COMPLIANCE_SUBMISSION_HANDOFF_NO_FILE',
} as const;

// ── One case's submission ──────────────────────────────────────────────────────────────────────

/** The agency's answers a person records about a send (the record act's kinds; never acceptance). */
export type RecordedAgencyAnswer =
  | 'agency_acknowledgment'
  | 'agency_rejection'
  | 'agency_correction_request';

/** One reason Givmo can't send this filing yet, in the server's words. */
export interface SubmissionReadiness {
  code: string;
  message: string;
  /** The checklist items it names, by their index, where it names any. */
  checklistItems: number[];
}

/** Where the agency takes this filing by email, from its rule as Givmo recorded it. */
/**
 * Where the agency's email address comes from (CCM-ST2-SUBMIT-CITE; UX slice 3's P2): the agency's
 * own sentence giving it, read from one source of the rule's, with that source's page, its short
 * citation, the date it shows and a link to it. Every field is that one source's, never another's.
 */
export interface SubmissionDestinationCitation {
  /** The agency's own sentence, verbatim. */
  sentence: string;
  /** The page of the source that prints it, where the source is paged. */
  page?: number;
  /**
   * A short citation for the source (the citation the source line shows; D-CCM-34). Absent, the
   * line shows no citation text: the sentence, its date and the link only.
   */
  displayCitation?: string;
  /** The date the source shows (a civil ISO date), as the backend resolved it (CCM-BE12). */
  sourceDate: string;
  /**
   * What happened on that date. ⚠ Anything this build cannot read is `undefined`, and the line then
   * takes the less-asserting wording: a source is never called checked unless the wire says so.
   */
  sourceDateBasis?: 'checked_live' | 'research_notes';
  /** A link to the source the sentence was read from; only a web address is a link. */
  sourceUrl?: string;
  /** CCM-OPS1-STORE: the day a check found a possible change to one of the rule's sources. */
  sourceChangeFoundOn?: string;
  /** Whether every possible change under review sits on this source (`false`: another one). */
  sourceChangeOnCitedSource?: boolean;
}

export interface SubmissionDestination {
  transport: string;
  address: string;
  agency: string;
  jurisdictionName: string;
  /** Absent on an older backend, and while the rule cites no source for the address. */
  citation?: SubmissionDestinationCitation;
}

/** One file of the package, in the order it is attached. */
export interface SubmissionPackageEntry {
  position: number;
  documentId: string;
  versionId: string;
  role: PackageRole | 'unknown';
  title: string;
  checklistItemIndex?: number;
  checklistItem?: string;
  fileName: string;
  byteSize: number;
  pageCount: number;
  contentSha256: string;
}

/** The files exactly as they would go, the SHA-256 that binds them, and the most one email carries. */
export interface SubmissionPackage {
  entries: SubmissionPackageEntry[];
  packageSha256: string;
  totalByteSize: number;
  maxByteSize: number;
}

/** The email's words exactly as they go, made by Givmo from a fixed template. */
export interface SubmissionTransmittal {
  templateId: string;
  subject: string;
  body: string;
  transmittalSha256: string;
}

export interface SubmissionFeeStatement {
  kind: FeeStatementKind | 'unknown';
  /** The amount as the form states it (`"0"`, `"200"`), where it states one. */
  amount?: string;
  /** The answer as written on the form, where it isn't a plain amount ("N/A"). */
  written?: string;
  /** The form's own number for its fee item. */
  itemNumber?: string;
}

/** The completed request whose signed forms the package carries. */
export interface SubmissionSignature {
  requestId: string;
  completedAt?: string;
  factualLineApplies: boolean;
  /** The served factual line (D-SIGN-3), shown only where `factualLineApplies`. */
  factualLine?: string;
}

export interface SubmissionReplyContact {
  source: ReplyContactSource;
  address: string;
}

/** Who the email actually goes to in this environment, and from which address. */
export interface SubmissionRecipients {
  to: string[];
  cc: string[];
  replyTo?: string;
  /** Outside production every filing email goes to one test address, and no copy goes out. */
  sinkApplied: boolean;
  fromAddress: string;
  fromDisplayName: string;
}

/** The words the act is made by, shown directly above the button (served; approved words). */
export interface SubmissionStatement {
  statementId: string;
  text: string;
}

/**
 * The words a person authorizes by to let their own AI send this exact package, once (frame 74's
 * act; served, approved words), and how long such an authorization lasts, in seconds: the page's
 * "works until" is that from now, on the viewer's clock (CCM-R1-SUBMIT-MCP).
 */
export interface SubmissionConnectorStatement {
  statementId: string;
  text: string;
  lifetimeSeconds: number;
}

/** One file an authorization binds. */
export interface SubmissionBoundEntry {
  position: number;
  documentId: string;
  versionId: string;
  contentSha256: string;
  byteSize: number;
  role: PackageRole | 'unknown';
  fileName: string;
}

/** The case's latest authorization to send, with everything it binds and how it stands. */
export interface SubmissionAuthorization {
  authorizationId: string;
  state: SubmissionAuthorizationState | 'unknown';
  /**
   * Who sends under it (CCM-R1-DESK-WEB): `givmo` for an authorization the organization made for
   * Givmo's filing desk to send; `organization` (and an older read's none) for its own.
   */
  mode?: 'organization' | 'givmo';
  destination: string;
  package: SubmissionBoundEntry[];
  packageSha256: string;
  packageByteSize: number;
  transmittalSubject: string;
  transmittalBody: string;
  transmittalSha256: string;
  fromAddress: string;
  fromDisplayName: string;
  replyContact: string;
  replyContactSource: ReplyContactSource | 'unknown';
  feeStatement: SubmissionFeeStatement;
  statementId: string;
  authorizedByUserId: string;
  authorizedAt?: string;
  /** The authorizer's last sign-in as the authorization recorded it. */
  authTime?: string;
  /** How they confirmed it where the record names a way ("with a passkey"), in the record's words. */
  signInMethodWords?: string;
  /** The organization's sign-in check then ("no extra sign-in check"), in the record's words. */
  identityLevelWords?: string;
  expiresAt?: string;
  consumedAt?: string;
  revokedByUserId?: string;
  revokedAt?: string;
  expiredAt?: string;
  /** The number the next send under it takes: what a send echoes. */
  nextAttemptOrdinal: number;
  /**
   * The checklist as it stood when this was authorized (CCM-R1-SUBMIT-SENT-SUMMARY); absent for an
   * authorization made before Givmo kept it.
   */
  checklist?: SubmissionChecklistEntry[];
  /** Whether its author let their own AI send it, once (CCM-R1-SUBMIT-MCP); unreadable is no. */
  connectorDispatchAllowed: boolean;
}

/** One checklist item's state as a record kept it. */
export interface SubmissionChecklistEntry {
  attachmentIndex: number;
  status: ChecklistRecordStatus | 'unknown';
}

/** One send under an authorization. */
export interface SubmissionAttempt {
  attemptId: string;
  authorizationId: string;
  ordinal: number;
  state: SubmissionAttemptState | 'unknown';
  dispatchedThrough: string;
  dispatchedByUserId: string;
  environment: string;
  sinkApplied: boolean;
  /** The addresses it actually went to, as recorded before it left. */
  to: string[];
  cc: string[];
  replyTo?: string;
  fromAddress: string;
  committedAt?: string;
  stampedAt?: string;
  providerMessageId?: string;
  refusalReason?: SubmissionRefusalReason;
  uncertainCause?: UncertainCause;
  reconciledByUserId?: string;
  reconciledAt?: string;
  reconciliationBasis?: ReconciliationBasis;
  reconciliationNote?: string;
  /**
   * On a send the organization's connected AI made (`dispatchedThrough` `connector`): what the
   * person asked their AI, as the AI reported it — the organization's own account, which Givmo
   * doesn't check (CCM-R1-SUBMIT-MCP).
   */
  connectorChatRequest?: string;
}

/** Whether a send was made by the organization's connected AI (CCM-R1-SUBMIT-MCP). */
export function sentByConnector(attempt: SubmissionAttempt): boolean {
  return attempt.dispatchedThrough === 'connector';
}

/** What the viewer can do here now; absent is no. */
export interface SubmissionAbilities {
  authorize: boolean;
  dispatch: boolean;
  revoke: boolean;
  reconcile: boolean;
  /** Record what the organization sent itself (a writer; CCM-R1-SUBMIT-HANDOFF). */
  recordHandoff: boolean;
  /** Whether the viewer's own connected AI may send it now (CCM-R1-SUBMIT-MCP). */
  connectorDispatch: boolean;
  /** Record what the agency told the organization about a send (CCM-R1-RESULTS). */
  recordEvidence: boolean;
  /** Say a report or a record about a send is wrong (CCM-R1-RESULTS). */
  answerEvidence: boolean;
}

/** The agency's form's own words, verbatim, with the cite and the page they are printed on. */
export interface SubmissionQuote {
  text: string;
  cite: string;
  page?: number;
}

/** One way the agency takes this filing, as its sources record it. */
export interface SubmissionChannelInstruction {
  channel: SubmissionChannel | 'unknown';
  /** The address to mail it to, or the email address. */
  address?: string;
  portalName?: string;
  portalUrl?: string;
  /** The form's own words for this destination, only where they ARE the rule's destination. */
  quote?: SubmissionQuote;
  /** What the sources say about this way beyond its destination (a cited note). */
  note?: string;
}

/** How the agency's sources say this filing is sent, in the form's own words where it prints them. */
export interface SubmissionInstructions {
  channels: SubmissionChannelInstruction[];
  feePayment?: SubmissionQuote;
  signer?: SubmissionQuote;
  replies?: SubmissionQuote;
  response?: SubmissionQuote;
  /** Where the agency's form these words come from was taken, and when (an ISO date). */
  formSource?: { sourceUrl: string; checkedOn: string };
}

/**
 * What the organization recorded it sent itself: how and when, and what: the package exactly as the
 * case composed it (its files and their SHA-256), or one of its own documents; the proof of sending.
 * The organization's own record of sending: never a filing Givmo sent, never a sign it arrived.
 */
export interface SubmissionHandoff {
  handoffId: string;
  filingCaseId: string;
  channel: SubmissionChannel | 'unknown';
  /** The civil date it went (YYYY-MM-DD). */
  sentOn: string;
  content: HandoffContent | 'unknown';
  signingRequestId?: string;
  package: SubmissionBoundEntry[];
  packageSha256?: string;
  packageByteSize?: number;
  checklist?: SubmissionChecklistEntry[];
  sentArtifactId?: string;
  proofArtifactId?: string;
  recordedByUserId: string;
  recordedAt?: string;
  /**
   * CCM-R1-AI-SETTING-VIA-AI: how the record was made, `web` (a person in Givmo) or `connector`
   * (through their connected AI), not the way it went (`channel`). Absent from a backend that
   * doesn't say, and on a record from before it was kept.
   */
  recordedChannel?: string;
}

/**
 * One report or record about a send, or about a record of sending (CCM-R1-RESULTS): what it says,
 * where it came from, whom it is about, when it happened and was recorded, who recorded it, with its
 * document and note, and the record that answers it, if one does. An unknown kind or source is kept
 * as `unknown`, and nothing reads it.
 */
export interface SubmissionEvidence {
  evidenceId: string;
  attemptId?: string;
  handoffId?: string;
  kind: SubmissionEvidenceKind | 'unknown';
  source: SubmissionEvidenceSource | 'unknown';
  /** Whom a provider's report is about: the agency's address (`destination`), or another. */
  recipientRole?: string;
  artifactId?: string;
  recordedByUserId?: string;
  note?: string;
  occurredAt?: string;
  recordedAt?: string;
  /** The record this one says is wrong (an answer). */
  answersEvidenceId?: string;
  /** The answer that says this one is wrong. */
  answeredByEvidenceId?: string;
  /**
   * The civil day it happened, in its record's own time zone (YYYY-MM-DD): for a person's record
   * of the agency's answer, the day they gave (CCM-R1-RESULTS-ANSWER-DAY).
   */
  occurredOn?: string;
  /** The civil day it was recorded, in its record's own time zone (YYYY-MM-DD). */
  recordedOn?: string;
}

/**
 * What is known so far about one send, or one record of sending (CCM-R1-RESULTS): what it reads as,
 * the evidence that decides it, and, where that is a person's record or the confirmation someone
 * attached, who recorded it, when, and on which day in the filing's own time zone. An unknown state
 * reads as `unknown`: never as the more confident answer.
 */
export interface SubmissionFollowThrough {
  attemptId?: string;
  handoffId?: string;
  state: SubmissionFollowThroughState | 'unknown';
  evidenceId?: string;
  recordedByUserId?: string;
  recordedAt?: string;
  /** The civil day it was recorded, in the filing's own time zone (YYYY-MM-DD). */
  recordedOn?: string;
  /** The filed date the register records with the confirmation (YYYY-MM-DD). */
  confirmedOn?: string;
}

export interface Submission {
  filingCaseId: string;
  ready: boolean;
  readiness: SubmissionReadiness[];
  destination?: SubmissionDestination;
  package?: SubmissionPackage;
  transmittal?: SubmissionTransmittal;
  feeStatement?: SubmissionFeeStatement;
  signature?: SubmissionSignature;
  /**
   * CCM-R1-SIGN-OUTSIDE-SEND-B: what executes the form as the package carries it, in the version's
   * own shape (`execution`): Givmo's signed final, or the copy signed outside Givmo with who signed
   * it as the organization states and its statement (W7); `null` while nothing executes the form.
   * `undefined` only where the server predates the field, which never sends a copy.
   */
  execution?: DocumentVersion | null;
  replyContacts: SubmissionReplyContact[];
  recipients?: SubmissionRecipients;
  statement?: SubmissionStatement;
  /** Frame 74's words for this package and their lifetime; absent where none is served. */
  connectorStatement?: SubmissionConnectorStatement;
  authorization?: SubmissionAuthorization;
  /** Every send of the case, under any of its authorizations, oldest first. */
  attempts: SubmissionAttempt[];
  abilities: SubmissionAbilities;
  /** How the agency's sources say it is sent; absent where the case's rule isn't known. */
  instructions?: SubmissionInstructions;
  /** What the organization recorded it sent itself, oldest first. */
  handoffs: SubmissionHandoff[];
  /** Every report and record about the case's sends, oldest first (CCM-R1-RESULTS). */
  evidence: SubmissionEvidence[];
  /** What is known so far about each send that reads as sent, and each record of sending. */
  followThrough: SubmissionFollowThrough[];
}

function decodeReadiness(raw: unknown): SubmissionReadiness | undefined {
  if (!isRecord(raw)) return undefined;
  const code = optionalStr(raw.code);
  const message = optionalStr(raw.message);
  if (!code || !message) return undefined;
  return {
    code,
    message,
    checklistItems: Array.isArray(raw.checklist_items)
      ? raw.checklist_items.filter((index): index is number => Number.isInteger(index))
      : [],
  };
}

/** A civil ISO date (`2026-09-15`): anything else is no date this page can show. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function decodeCitation(raw: unknown): SubmissionDestinationCitation | undefined {
  if (!isRecord(raw)) return undefined;
  const sentence = optionalStr(raw.quoted_source_text)?.trim();
  const sourceDate = optionalStr(raw.source_date);
  // No sentence or no date it can show: no citation, and the row keeps the line it drew before.
  if (!sentence || sourceDate === undefined || !ISO_DATE.test(sourceDate)) return undefined;
  const page = optionalInt(raw.page);
  const displayCitation = optionalStr(raw.display_citation)?.trim();
  const sourceDateBasis = oneOf(raw.source_date_basis, ['checked_live', 'research_notes'] as const);
  const sourceUrl = optionalStr(raw.source_url);
  const foundOn = optionalStr(raw.source_change_found_on);
  const onCitedSource = raw.source_change_on_cited_source;
  return {
    sentence,
    sourceDate,
    ...(page !== undefined && page >= 1 ? { page } : {}),
    ...(displayCitation ? { displayCitation } : {}),
    ...(sourceDateBasis !== undefined ? { sourceDateBasis } : {}),
    // Only a web address is a link: anything else is not followed.
    ...(sourceUrl !== undefined && /^https?:\/\//.test(sourceUrl) ? { sourceUrl } : {}),
    ...(foundOn !== undefined && ISO_DATE.test(foundOn) ? { sourceChangeFoundOn: foundOn } : {}),
    ...(typeof onCitedSource === 'boolean' ? { sourceChangeOnCitedSource: onCitedSource } : {}),
  };
}

function decodeDestination(raw: unknown): SubmissionDestination | undefined {
  if (!isRecord(raw)) return undefined;
  const address = optionalStr(raw.address);
  if (!address) return undefined;
  const citation = decodeCitation(raw.citation);
  return {
    transport: str(raw.transport),
    address,
    agency: str(raw.agency),
    jurisdictionName: str(raw.jurisdiction_name),
    ...(citation !== undefined ? { citation } : {}),
  };
}

function role(value: unknown): PackageRole | 'unknown' {
  return oneOf(value, ['signed_form', 'attachment'] as const) ?? 'unknown';
}

function decodePackageEntry(raw: unknown): SubmissionPackageEntry | undefined {
  if (!isRecord(raw)) return undefined;
  const documentId = optionalStr(raw.document_id);
  const versionId = optionalStr(raw.version_id);
  const contentSha256 = optionalStr(raw.content_sha256);
  const fileName = optionalStr(raw.file_name);
  if (!documentId || !versionId || !contentSha256 || !fileName) return undefined;
  const checklistItemIndex = optionalInt(raw.checklist_item_index);
  const checklistItem = optionalStr(raw.checklist_item);
  return {
    position: optionalInt(raw.position) ?? 0,
    documentId,
    versionId,
    role: role(raw.role),
    title: str(raw.title),
    ...(checklistItemIndex !== undefined ? { checklistItemIndex } : {}),
    ...(checklistItem !== undefined ? { checklistItem } : {}),
    fileName,
    byteSize: optionalInt(raw.byte_size) ?? 0,
    pageCount: optionalInt(raw.page_count) ?? 0,
    contentSha256,
  };
}

function decodePackage(raw: unknown): SubmissionPackage | undefined {
  if (!isRecord(raw)) return undefined;
  const packageSha256 = optionalStr(raw.package_sha256);
  if (!packageSha256) return undefined;
  return {
    entries: list(raw.entries, decodePackageEntry).sort((a, b) => a.position - b.position),
    packageSha256,
    totalByteSize: optionalInt(raw.total_byte_size) ?? 0,
    maxByteSize: optionalInt(raw.max_byte_size) ?? 0,
  };
}

function decodeTransmittal(raw: unknown): SubmissionTransmittal | undefined {
  if (!isRecord(raw)) return undefined;
  const subject = optionalStr(raw.subject);
  const body = optionalStr(raw.body);
  const transmittalSha256 = optionalStr(raw.transmittal_sha256);
  if (!subject || !body || !transmittalSha256) return undefined;
  return { templateId: str(raw.template_id), subject, body, transmittalSha256 };
}

function decodeFeeStatement(raw: unknown): SubmissionFeeStatement | undefined {
  if (!isRecord(raw)) return undefined;
  const amount = optionalStr(raw.amount);
  const written = optionalStr(raw.written);
  const itemNumber = optionalStr(raw.item_number);
  return {
    kind: oneOf(raw.kind, FEE_STATEMENT_KINDS) ?? 'unknown',
    ...(amount !== undefined ? { amount } : {}),
    ...(written !== undefined ? { written } : {}),
    ...(itemNumber !== undefined ? { itemNumber } : {}),
  };
}

function decodeSignature(raw: unknown): SubmissionSignature | undefined {
  if (!isRecord(raw)) return undefined;
  const requestId = optionalStr(raw.request_id);
  if (!requestId) return undefined;
  const completedAt = optionalStr(raw.completed_at);
  const factualLine = optionalStr(raw.factual_line);
  return {
    requestId,
    ...(completedAt !== undefined ? { completedAt } : {}),
    factualLineApplies: raw.factual_line_applies === true,
    ...(factualLine !== undefined ? { factualLine } : {}),
  };
}

function decodeReplyContact(raw: unknown): SubmissionReplyContact | undefined {
  if (!isRecord(raw)) return undefined;
  const source = oneOf(raw.source, REPLY_CONTACT_SOURCES);
  const address = optionalStr(raw.address);
  return source !== undefined && address !== undefined ? { source, address } : undefined;
}

function decodeRecipients(raw: unknown): SubmissionRecipients | undefined {
  if (!isRecord(raw)) return undefined;
  const fromAddress = optionalStr(raw.from_address);
  if (!fromAddress || typeof raw.sink_applied !== 'boolean') return undefined;
  const replyTo = optionalStr(raw.reply_to);
  return {
    to: strings(raw.to),
    cc: strings(raw.cc),
    ...(replyTo !== undefined ? { replyTo } : {}),
    sinkApplied: raw.sink_applied,
    fromAddress,
    fromDisplayName: str(raw.from_display_name),
  };
}

function decodeStatement(raw: unknown): SubmissionStatement | undefined {
  if (!isRecord(raw)) return undefined;
  const statementId = optionalStr(raw.statement_id);
  const text = optionalStr(raw.text);
  return statementId !== undefined && text !== undefined ? { statementId, text } : undefined;
}

function decodeConnectorStatement(raw: unknown): SubmissionConnectorStatement | undefined {
  const statement = decodeStatement(raw);
  if (statement === undefined || !isRecord(raw)) return undefined;
  const lifetimeSeconds = optionalInt(raw.lifetime_seconds);
  // No lifetime, no "works until": the offer is withheld rather than a date guessed.
  return lifetimeSeconds !== undefined && lifetimeSeconds > 0
    ? { ...statement, lifetimeSeconds }
    : undefined;
}

function decodeBoundEntry(raw: unknown): SubmissionBoundEntry | undefined {
  if (!isRecord(raw)) return undefined;
  const documentId = optionalStr(raw.document_id);
  const versionId = optionalStr(raw.version_id);
  const contentSha256 = optionalStr(raw.content_sha256);
  const fileName = optionalStr(raw.file_name);
  if (!documentId || !versionId || !contentSha256 || !fileName) return undefined;
  return {
    position: optionalInt(raw.position) ?? 0,
    documentId,
    versionId,
    contentSha256,
    byteSize: optionalInt(raw.byte_size) ?? 0,
    role: role(raw.role),
    fileName,
  };
}

function decodeAuthorization(raw: unknown): SubmissionAuthorization | undefined {
  if (!isRecord(raw)) return undefined;
  const authorizationId = optionalStr(raw.authorization_id);
  const packageSha256 = optionalStr(raw.package_sha256);
  const authorizedByUserId = optionalStr(raw.authorized_by_user_id);
  const nextAttemptOrdinal = optionalInt(raw.next_attempt_ordinal);
  if (
    !authorizationId ||
    !packageSha256 ||
    !authorizedByUserId ||
    nextAttemptOrdinal === undefined
  ) {
    return undefined;
  }
  const optional = (key: string, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined ? { [key]: decoded } : {};
  };
  const mode = oneOf(raw.mode, ['organization', 'givmo'] as const);
  return {
    authorizationId,
    state: oneOf(raw.state, SUBMISSION_AUTHORIZATION_STATES) ?? 'unknown',
    ...(mode !== undefined ? { mode } : {}),
    destination: str(raw.destination),
    package: list(raw.package, decodeBoundEntry).sort((a, b) => a.position - b.position),
    packageSha256,
    packageByteSize: optionalInt(raw.package_byte_size) ?? 0,
    transmittalSubject: str(raw.transmittal_subject),
    transmittalBody: str(raw.transmittal_body),
    transmittalSha256: str(raw.transmittal_sha256),
    fromAddress: str(raw.from_address),
    fromDisplayName: str(raw.from_display_name),
    replyContact: str(raw.reply_contact),
    replyContactSource: oneOf(raw.reply_contact_source, REPLY_CONTACT_SOURCES) ?? 'unknown',
    feeStatement: decodeFeeStatement(raw.fee_statement) ?? { kind: 'unknown' },
    statementId: str(raw.statement_id),
    authorizedByUserId,
    ...optional('authorizedAt', raw.authorized_at),
    ...optional('authTime', raw.auth_time),
    ...optional('signInMethodWords', raw.sign_in_method_words),
    ...optional('identityLevelWords', raw.identity_level_words),
    ...optional('expiresAt', raw.expires_at),
    ...optional('consumedAt', raw.consumed_at),
    ...optional('revokedByUserId', raw.revoked_by_user_id),
    ...optional('revokedAt', raw.revoked_at),
    ...optional('expiredAt', raw.expired_at),
    nextAttemptOrdinal,
    ...(Array.isArray(raw.checklist)
      ? { checklist: list(raw.checklist, decodeChecklistEntry) }
      : {}),
    connectorDispatchAllowed: raw.connector_dispatch_allowed === true,
  };
}

function decodeChecklistEntry(raw: unknown): SubmissionChecklistEntry | undefined {
  if (!isRecord(raw)) return undefined;
  const attachmentIndex = optionalInt(raw.attachment_index);
  if (attachmentIndex === undefined) return undefined;
  return {
    attachmentIndex,
    status: oneOf(raw.status, CHECKLIST_RECORD_STATUSES) ?? 'unknown',
  };
}

function decodeAttempt(raw: unknown): SubmissionAttempt | undefined {
  if (!isRecord(raw)) return undefined;
  const attemptId = optionalStr(raw.attempt_id);
  const authorizationId = optionalStr(raw.authorization_id);
  const ordinal = optionalInt(raw.ordinal);
  const dispatchedByUserId = optionalStr(raw.dispatched_by_user_id);
  if (!attemptId || !authorizationId || ordinal === undefined || !dispatchedByUserId) {
    return undefined;
  }
  const replyTo = optionalStr(raw.reply_to);
  const committedAt = optionalStr(raw.committed_at);
  const stampedAt = optionalStr(raw.stamped_at);
  const providerMessageId = optionalStr(raw.provider_message_id);
  const refusalReason = oneOf(raw.refusal_reason, [
    'provider_busy',
    'provider_refused',
    'not_built',
  ] as const);
  const uncertainCause = oneOf(raw.uncertain_cause, ['provider_unclear', 'stamp_missing'] as const);
  const reconciledByUserId = optionalStr(raw.reconciled_by_user_id);
  const reconciledAt = optionalStr(raw.reconciled_at);
  const reconciliationBasis = oneOf(raw.reconciliation_basis, [
    'copy_received',
    'provider_activity',
    'not_received',
    'provider_event',
  ] as const);
  const reconciliationNote = optionalStr(raw.reconciliation_note);
  const connectorChatRequest = optionalStr(raw.connector_chat_request);
  return {
    attemptId,
    authorizationId,
    ordinal,
    state: oneOf(raw.state, SUBMISSION_ATTEMPT_STATES) ?? 'unknown',
    dispatchedThrough: str(raw.dispatched_through),
    dispatchedByUserId,
    environment: str(raw.environment),
    // Unreadable reads as a send outside production: never as an agency's address that wasn't one.
    sinkApplied: raw.sink_applied !== false,
    to: strings(raw.to),
    cc: strings(raw.cc),
    ...(replyTo !== undefined ? { replyTo } : {}),
    fromAddress: str(raw.from_address),
    ...(committedAt !== undefined ? { committedAt } : {}),
    ...(stampedAt !== undefined ? { stampedAt } : {}),
    ...(providerMessageId !== undefined ? { providerMessageId } : {}),
    ...(refusalReason !== undefined ? { refusalReason } : {}),
    ...(uncertainCause !== undefined ? { uncertainCause } : {}),
    ...(reconciledByUserId !== undefined ? { reconciledByUserId } : {}),
    ...(reconciledAt !== undefined ? { reconciledAt } : {}),
    ...(reconciliationBasis !== undefined ? { reconciliationBasis } : {}),
    ...(reconciliationNote !== undefined ? { reconciliationNote } : {}),
    ...(connectorChatRequest !== undefined ? { connectorChatRequest } : {}),
  };
}

function decodeAbilities(raw: unknown): SubmissionAbilities {
  if (!isRecord(raw)) {
    return {
      authorize: false,
      dispatch: false,
      revoke: false,
      reconcile: false,
      recordHandoff: false,
      connectorDispatch: false,
      recordEvidence: false,
      answerEvidence: false,
    };
  }
  return {
    authorize: raw.authorize === true,
    dispatch: raw.dispatch === true,
    revoke: raw.revoke === true,
    reconcile: raw.reconcile === true,
    recordHandoff: raw.record_handoff === true,
    connectorDispatch: raw.connector_dispatch === true,
    recordEvidence: raw.record_evidence === true,
    answerEvidence: raw.answer_evidence === true,
  };
}

function decodeEvidence(raw: unknown): SubmissionEvidence | undefined {
  if (!isRecord(raw)) return undefined;
  const evidenceId = optionalStr(raw.evidence_id);
  if (!evidenceId) return undefined;
  const optional = (key: keyof SubmissionEvidence, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined ? { [key]: decoded } : {};
  };
  const day = (key: keyof SubmissionEvidence, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined && ISO_DATE.test(decoded) ? { [key]: decoded } : {};
  };
  return {
    evidenceId,
    ...optional('attemptId', raw.attempt_id),
    ...optional('handoffId', raw.handoff_id),
    kind: oneOf(raw.kind, SUBMISSION_EVIDENCE_KINDS) ?? 'unknown',
    source: oneOf(raw.source, SUBMISSION_EVIDENCE_SOURCES) ?? 'unknown',
    ...optional('recipientRole', raw.recipient_role),
    ...optional('artifactId', raw.artifact_id),
    ...optional('recordedByUserId', raw.recorded_by_user_id),
    ...optional('note', raw.note),
    ...optional('occurredAt', raw.occurred_at),
    ...optional('recordedAt', raw.recorded_at),
    ...optional('answersEvidenceId', raw.answers_evidence_id),
    ...optional('answeredByEvidenceId', raw.answered_by_evidence_id),
    ...day('occurredOn', raw.occurred_on),
    ...day('recordedOn', raw.recorded_on),
  };
}

/** One send's follow-through; a body naming neither its send nor its record of sending is none. */
export function decodeFollowThrough(raw: unknown): SubmissionFollowThrough | undefined {
  if (!isRecord(raw)) return undefined;
  const attemptId = optionalStr(raw.attempt_id);
  const handoffId = optionalStr(raw.handoff_id);
  if (!attemptId && !handoffId) return undefined;
  const optional = (key: keyof SubmissionFollowThrough, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined ? { [key]: decoded } : {};
  };
  const day = (key: keyof SubmissionFollowThrough, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined && ISO_DATE.test(decoded) ? { [key]: decoded } : {};
  };
  return {
    ...(attemptId ? { attemptId } : {}),
    ...(handoffId ? { handoffId } : {}),
    state: oneOf(raw.follow_through, SUBMISSION_FOLLOW_THROUGHS) ?? 'unknown',
    ...optional('evidenceId', raw.evidence_id),
    ...optional('recordedByUserId', raw.recorded_by_user_id),
    ...optional('recordedAt', raw.recorded_at),
    ...day('recordedOn', raw.recorded_on),
    ...day('confirmedOn', raw.confirmed_on),
  };
}

/**
 * One filing case's latest send, or latest record of sending, with what is known about it so far
 * (CCM-R1-RESULTS's organization-wide read): the panel, the Calendar and Filings read it.
 */
export interface CaseFollowThrough {
  filingCaseId: string;
  obligationId: string;
  renewalCycleId?: string;
  periodStart?: string;
  periodEnd?: string;
  /** The day the latest send went (YYYY-MM-DD). */
  sentOn: string;
  /** How many of the case's sends went before it: one or more reads "Sent again". */
  earlierSends: number;
  latest: SubmissionFollowThrough;
}

/** One row of the organization-wide read, or `undefined` for a row that can't be placed. */
export function decodeCaseFollowThrough(raw: unknown): CaseFollowThrough | undefined {
  if (!isRecord(raw)) return undefined;
  const filingCaseId = optionalStr(raw.filing_case_id);
  const obligationId = optionalStr(raw.obligation_id);
  const sentOn = optionalStr(raw.sent_on);
  const latest = decodeFollowThrough(raw.latest);
  if (!filingCaseId || !obligationId || !sentOn || !ISO_DATE.test(sentOn) || !latest) {
    return undefined;
  }
  const renewalCycleId = optionalStr(raw.renewal_cycle_id);
  const periodStart = optionalStr(raw.period_start);
  const periodEnd = optionalStr(raw.period_end);
  const earlier = optionalInt(raw.earlier_sends);
  return {
    filingCaseId,
    obligationId,
    ...(renewalCycleId !== undefined ? { renewalCycleId } : {}),
    ...(periodStart !== undefined && ISO_DATE.test(periodStart) ? { periodStart } : {}),
    ...(periodEnd !== undefined && ISO_DATE.test(periodEnd) ? { periodEnd } : {}),
    sentOn,
    earlierSends: earlier !== undefined && earlier > 0 ? earlier : 0,
    latest,
  };
}

function decodeQuote(raw: unknown): SubmissionQuote | undefined {
  if (!isRecord(raw)) return undefined;
  const text = optionalStr(raw.text);
  if (text === undefined) return undefined;
  const page = optionalInt(raw.page);
  return { text, cite: str(raw.cite), ...(page !== undefined ? { page } : {}) };
}

function decodeChannelInstruction(raw: unknown): SubmissionChannelInstruction | undefined {
  if (!isRecord(raw)) return undefined;
  const address = optionalStr(raw.address);
  const portalName = optionalStr(raw.portal_name);
  const portalUrl = optionalStr(raw.portal_url);
  const quote = decodeQuote(raw.quote);
  const note = optionalStr(raw.note);
  return {
    channel: oneOf(raw.channel, SUBMISSION_CHANNELS) ?? 'unknown',
    ...(address !== undefined ? { address } : {}),
    ...(portalName !== undefined ? { portalName } : {}),
    // Only a web address is a link: anything else is not followed.
    ...(portalUrl !== undefined && /^https?:\/\//.test(portalUrl) ? { portalUrl } : {}),
    ...(quote !== undefined ? { quote } : {}),
    ...(note !== undefined ? { note } : {}),
  };
}

function decodeInstructions(raw: unknown): SubmissionInstructions | undefined {
  if (!isRecord(raw)) return undefined;
  const optionalQuote = (key: keyof SubmissionInstructions, value: unknown) => {
    const decoded = decodeQuote(value);
    return decoded !== undefined ? { [key]: decoded } : {};
  };
  const source = isRecord(raw.form_source) ? raw.form_source : undefined;
  const sourceUrl = source !== undefined ? optionalStr(source.source_url) : undefined;
  const checkedOn = source !== undefined ? optionalStr(source.checked_on) : undefined;
  return {
    channels: list(raw.channels, decodeChannelInstruction),
    ...optionalQuote('feePayment', raw.fee_payment),
    ...optionalQuote('signer', raw.signer),
    ...optionalQuote('replies', raw.replies),
    ...optionalQuote('response', raw.response),
    ...(sourceUrl !== undefined && checkedOn !== undefined && /^https?:\/\//.test(sourceUrl)
      ? { formSource: { sourceUrl, checkedOn } }
      : {}),
  };
}

function decodeHandoff(raw: unknown): SubmissionHandoff | undefined {
  if (!isRecord(raw)) return undefined;
  const handoffId = optionalStr(raw.handoff_id);
  const filingCaseId = optionalStr(raw.filing_case_id);
  const sentOn = optionalStr(raw.sent_on);
  const recordedByUserId = optionalStr(raw.recorded_by_user_id);
  if (!handoffId || !filingCaseId || !sentOn || !recordedByUserId) return undefined;
  const optional = (key: string, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined ? { [key]: decoded } : {};
  };
  const packageByteSize = optionalInt(raw.package_byte_size);
  return {
    handoffId,
    filingCaseId,
    channel: oneOf(raw.channel, SUBMISSION_CHANNELS) ?? 'unknown',
    sentOn,
    content: oneOf(raw.content, HANDOFF_CONTENTS) ?? 'unknown',
    ...optional('signingRequestId', raw.signing_request_id),
    package: list(raw.package, decodeBoundEntry).sort((a, b) => a.position - b.position),
    ...optional('packageSha256', raw.package_sha256),
    ...(packageByteSize !== undefined ? { packageByteSize } : {}),
    ...(Array.isArray(raw.checklist)
      ? { checklist: list(raw.checklist, decodeChecklistEntry) }
      : {}),
    ...optional('sentArtifactId', raw.sent_artifact_id),
    ...optional('proofArtifactId', raw.proof_artifact_id),
    recordedByUserId,
    ...optional('recordedAt', raw.recorded_at),
    ...optional('recordedChannel', raw.recorded_channel),
  };
}

/** Oldest first: by when each was recorded. */
function byWhenRecorded(a: SubmissionHandoff, b: SubmissionHandoff): number {
  const at = (handoff: SubmissionHandoff) => {
    const ms = handoff.recordedAt !== undefined ? Date.parse(handoff.recordedAt) : NaN;
    return Number.isNaN(ms) ? 0 : ms;
  };
  return at(a) - at(b);
}

/** Oldest first: by when each send was committed, then by its number under its authorization. */
function byWhenCommitted(a: SubmissionAttempt, b: SubmissionAttempt): number {
  const at = (attempt: SubmissionAttempt) => {
    const ms = attempt.committedAt !== undefined ? Date.parse(attempt.committedAt) : NaN;
    return Number.isNaN(ms) ? 0 : ms;
  };
  return at(a) - at(b) || a.ordinal - b.ordinal;
}

/** One case's submission, or `undefined` for a body that doesn't name its case. */
export function decodeSubmission(body: unknown): Submission | undefined {
  if (!isRecord(body)) return undefined;
  const filingCaseId = optionalStr(body.filing_case_id);
  if (!filingCaseId) return undefined;
  const readiness = list(body.readiness, decodeReadiness);
  const destination = decodeDestination(body.destination);
  const pkg = decodePackage(body.package);
  const transmittal = decodeTransmittal(body.transmittal);
  const feeStatement = decodeFeeStatement(body.fee_statement);
  const signature = decodeSignature(body.signature);
  const recipients = decodeRecipients(body.recipients);
  const statement = decodeStatement(body.statement);
  const connectorStatement = decodeConnectorStatement(body.connector_statement);
  const authorization = decodeAuthorization(body.authorization);
  const instructions = decodeInstructions(body.instructions);
  return {
    filingCaseId,
    // Ready only as served AND with no reason listed: a body that says both never reads as ready.
    ready: body.ready === true && readiness.length === 0,
    readiness,
    ...(destination !== undefined ? { destination } : {}),
    ...(pkg !== undefined ? { package: pkg } : {}),
    ...(transmittal !== undefined ? { transmittal } : {}),
    ...(feeStatement !== undefined ? { feeStatement } : {}),
    ...(signature !== undefined ? { signature } : {}),
    ...('execution' in body ? { execution: decodeDocumentVersion(body.execution) ?? null } : {}),
    replyContacts: list(body.reply_contacts, decodeReplyContact),
    ...(recipients !== undefined ? { recipients } : {}),
    ...(statement !== undefined ? { statement } : {}),
    ...(connectorStatement !== undefined ? { connectorStatement } : {}),
    ...(authorization !== undefined ? { authorization } : {}),
    attempts: list(body.attempts, decodeAttempt).sort(byWhenCommitted),
    abilities: decodeAbilities(body.abilities),
    evidence: list(body.evidence, decodeEvidence),
    followThrough: list(body.follow_through, decodeFollowThrough),
    ...(instructions !== undefined ? { instructions } : {}),
    handoffs: list(body.handoffs, decodeHandoff).sort(byWhenRecorded),
  };
}

// ── The package as one zip (CCM-R1-SUBMIT-ZIP) ────────────────────────────────────────────────

/** A short-lived link to the package as one zip, and the name it downloads as. */
export interface SubmissionPackageFile {
  downloadUrl: string;
  fileName: string;
  packageSha256: string;
}

export function decodeSubmissionPackageFile(body: unknown): SubmissionPackageFile | undefined {
  if (!isRecord(body)) return undefined;
  const downloadUrl = optionalStr(body.download_url);
  if (!downloadUrl) return undefined;
  return {
    downloadUrl,
    fileName: str(body.file_name),
    packageSha256: str(body.package_sha256),
  };
}
